import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Collapse, Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import AddDomainModal from "../components/Domain/AddDomainModal";
import EditDomainModal from "../components/Domain/EditDomainModal";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import ViewOption from "../components/ViewOption";
import FilterWrapper from "../components/general/FiltersWrapper";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableTextField from "../components/general/ReusableTextField";
import ApproveModal from "../components/modal/ApproveModal";
import DeleteModal from "../components/modal/DeleteModal";
import HeavyTable from "../components/table/HeavyTable";
import { showWarningSwal } from "../functions/alert";
import { deleteDomain } from "../functions/deleteData";
import {
  buildQueryString,
  cancelAction,
  changeViewOptions,
  controlFilter,
  controlView,
  makeSureIsArray,
} from "../functions/general";
import { getDomainsAndTags } from "../functions/getData";
import { approveDomain } from "../functions/patchData";
import { showNotification } from "../functions/snackbar";

const domainHeader = [
  { id: "topic_domain", label: "Domain" },
  { id: "name", label: "Topic" },
  { id: "tag", label: "Tag" },
  { id: "created_by", label: "Created By" },
  { id: "last_modified_by", label: "Last Modified By" },
  { id: "status", label: "Status" },
];

export default function EnhancedDomain() {
  document.title = "Domains and Tags";
  const isMounted = useRef(false);
  const [header, setHeader] = useState(domainHeader);
  const [domainTable, setDomainTable] = useState();
  const [viewOptions, setViewOptions] = useState([
    { label: "Created By", state: true, key: "created_by_username" },
    { label: "Last Modified By", state: true, key: "modified_by_username" },
    { label: "Status", state: true, key: "status" },
  ]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [id, setId] = useState("");
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [onDelete, setOnDelete] = useState(false);
  const [onAddDomain, setOnAddDomain] = useState(false);
  const [onEditDomain, setOnEditDomain] = useState(false);
  const [onApprove, setOnApprove] = useState(false);
  const [domainOptions, setDomainOptions] = useState([]);
  const [sortAbleColumns, setSortableColumns] = useState([]);

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    domains: "",
    status: "",
    orderBy: "",
    orderDirection: "",
  });

  const handleView = () => controlView(viewing, setViewing, setFiltering);
  const handleFilter = () => controlFilter(filtering, setFiltering, setViewing);
  const cancelDelete = () => cancelAction(setOnDelete, setId);
  const cancelAddDomain = () => cancelAction(setOnAddDomain);
  const cancelEdit = () => cancelAction(setOnEditDomain, setId);
  const cancelApprove = () => cancelAction(setOnApprove, setId);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const afterFunction = (cancel, data) => {
    if ([200, 201].includes(data.status)) {
      if (controller.current) {
        setSelected([]);
        setSelecting([]);
        setLoading(true);

        const queryString = buildQueryString(queryParams);
        getDomainsAndTags(queryString, controller.current.signal).then(
          (res) => {
            setDomainTable(res);
            setSortableColumns(res?.sortable_columns);
            setLoading(false);
          }
        );
      }
      cancel();
      showNotification("success", data.message, enqueueSnackbar, closeSnackbar);
    }
  };

  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      domains: "",
      status: "",
      orderBy: "",
      orderDirection: "",
    });
  };

  const adjustEndpoint = (endpoint, value) => {
    const currentParams = { ...queryParams };

    // Remove the current filter value if it's being changed
    if (value !== currentParams.domains && currentParams.domains) {
      endpoint += `&domains=${currentParams.domains}`;
    }
    if (value !== currentParams.status && currentParams.status) {
      endpoint += `&status=${currentParams.status}`;
    }

    return endpoint;
  };

  const filters = [
    {
      label: "Domain",
      endpoint: "tag/list?get_filter=domains",
      value: queryParams.domains,
      onChange: (value) => updateQueryParams({ domains: value }),
      key: "domains",
      setValue: (value) => updateQueryParams({ domains: value }),
    },
    {
      label: "Status",
      endpoint: "tag/list?get_filter=status",
      value: queryParams.status,
      onChange: (value) => updateQueryParams({ status: value }),
      key: "status",
      setValue: (value) => updateQueryParams({ status: value }),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
    deleteDomain({ id: selected.length > 0 ? selected : [id] })
      .then((d) => {
        afterFunction(cancelDelete, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };

  const approveFunction = () => {
    setLoading(true);
    approveDomain({ id: selected.length > 0 ? selected : [id] })
      .then((d) => {
        afterFunction(cancelApprove, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };

  const handleSortingFunction = (sortingKey, direction) => {
    updateQueryParams({
      orderBy: sortingKey,
      orderDirection: direction,
    });
  };

  const onChangeViewOptions = (value) =>
    changeViewOptions(domainHeader, setViewOptions, setHeader, value);

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({ search: value });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({ page: pageNo });
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({ limits: numberOfRows });
  };

  const controller = useRef(null);
  const isFirstRender = useRef(true);

  // Effect for initial data fetch
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;
    isMounted.current = true;

    // Create a new abort controller for this effect
    controller.current = new AbortController();

    // Initial data fetch
    setSelected([]);
    setSelecting([]);
    setLoading(true);

    const queryString = buildQueryString(queryParams);
    getDomainsAndTags(queryString, controller.current.signal).then((res) => {
      setDomainTable(res);
      setSortableColumns(res?.sortable_columns);
      setLoading(false);
    });

    // Load domain options for modals
    getDomainsAndTags("?get_filter=domains", controller.current.signal)
      .then((d) => {
        const mappedOptions = d.map((domain) => {
          return {
            name: domain.filter_name,
            id: domain.filter_id,
          };
        });
        setDomainOptions(mappedOptions);
      })
      .catch((e) => console.log(e));

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
      isMounted.current = false;
    };
  }, []);

  // Effect for data fetching when query params change
  useEffect(() => {
    if (controller.current && !isFirstRender.current) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      getDomainsAndTags(queryString, controller.current.signal).then((res) => {
        setDomainTable(res);
        setSortableColumns(res?.sortable_columns);
        setLoading(false);
      });
    }
  }, [queryParams]);

  const onClickMassApprove = () => {
    return domainTable && selected.length > 0
      ? setOnApprove(true)
      : showWarningSwal(
          "Please select pending domains / tags only to mass approve"
        );
  };

  const onClickMassDelete = () => {
    return selected.length
      ? setOnDelete(true)
      : showWarningSwal("Please select at least one domain to mass delete");
  };

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={viewing}>
        <ViewOption
          onChangeRowsPerPage={onChangeRowsPerPage}
          viewOptions={viewOptions}
          rowsPerPage={rowsPerPage}
          onChangeViewOptions={onChangeViewOptions}
        />
      </Collapse>
      <Collapse in={filtering}>
        <FilterWrapper filters={filters} adjustEndpoint={adjustEndpoint} />
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Domain"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="View Options"
            state={viewing}
            fontSize={14}
            height={42}
            onClick={handleView}
            marginRight={10}
          />
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={handleFilter}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container>
        <NunitoText
          value="Domains and Tags"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <ReusableTextField
            type="search"
            width={500}
            height={60}
            bgColor="#F2F2F2"
            placeholder="Search using keyword"
            state={search}
            setState={onChangeSearch}
            onBlur
          />
          <div className="endBtnDiv">
            <ReusableButton
              text="Add Domain / Tag"
              fontSize={15}
              bgColor="#5E75C3"
              height={46}
              marginRight={20}
              onClick={() => setOnAddDomain(true)}
              iconType="add"
            />
            <ReusableButton
              text="Mass Delete"
              fontSize={15}
              bgColor="#E83042"
              height={46}
              marginRight={20}
              onClick={() => onClickMassDelete()}
              iconType="delete"
            />
            <ReusableButton
              text="Mass Approve"
              fontSize={15}
              bgColor="#5E75C3"
              height={46}
              marginRight={20}
              onClick={() => onClickMassApprove()}
              iconType="check"
            />
          </div>
        </Grid>
        <NunitoText
          value='Search only in "Domain", "Topic", and "Tag" column'
          fontSize={20}
          fontWeight={400}
          italic
          color="#144A94"
        />
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          domainTable &&
          makeSureIsArray(domainTable.data).length && (
            <HeavyTable
              handleSortingFunction={handleSortingFunction}
              sortableKeys={sortAbleColumns}
              headers={header}
              list={domainTable}
              title="domain"
              rowsPerPage={rowsPerPage}
              setOnDelete={setOnDelete}
              setOnApprove={setOnApprove}
              setOnEdit={setOnEditDomain}
              onChangePage={onChangePage}
              fixed={["domain_name", "name", "tag"]}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={setSelected}
              setId={setId}
            />
          )}
        {!loadingTable &&
          domainTable &&
          !makeSureIsArray(domainTable.data).length && <NoData height={600} />}
      </Grid>
      <Modal open={onDelete} onClose={() => cancelDelete()}>
        <>
          {Boolean(domainTable) && (
            <DeleteModal
              table={domainTable}
              id={id}
              selected={selected}
              deleteFunction={deleteFunction}
              cancelDelete={cancelDelete}
            />
          )}
        </>
      </Modal>
      <Modal open={onApprove} onClose={() => cancelApprove()}>
        <>
          {Boolean(domainTable) && (
            <ApproveModal
              table={domainTable}
              id={id}
              selected={selected}
              approveFunction={approveFunction}
              cancelApprove={cancelApprove}
              object="domain/tags"
            />
          )}
        </>
      </Modal>
      <Modal open={onAddDomain} onClose={() => cancelAddDomain()}>
        <>
          <AddDomainModal
            setOnAddDomain={setOnAddDomain}
            afterAddingDomain={() => {
              const queryString = buildQueryString(queryParams);
              getDomainsAndTags(queryString, controller.current.signal).then(
                (res) => {
                  setDomainTable(res);
                  setSortableColumns(res?.sortable_columns);
                }
              );
            }}
            domainOptions={domainOptions}
          />
        </>
      </Modal>
      <Modal open={onEditDomain} onClose={() => cancelEdit()}>
        <>
          {domainTable && (
            <EditDomainModal
              table={domainTable}
              id={id}
              afterEditingDomain={() => {
                const queryString = buildQueryString(queryParams);
                getDomainsAndTags(queryString, controller.current.signal).then(
                  (res) => {
                    setDomainTable(res);
                    setSortableColumns(res?.sortable_columns);
                  }
                );
              }}
              cancelEdit={cancelEdit}
            />
          )}
        </>
      </Modal>
    </Box>
  );
}
